import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Shield, Bug, Package, AlertTriangle, CheckCircle,
  ExternalLink, Copy, Clock, FileCode, Link2, Download, FileJson, FileText,
  GitBranch, Code, AlertOctagon, Globe, RefreshCw, GitPullRequest, TrendingUp, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  getScan,
  getSeverityColor,
  getSeverityBadgeColor,
  formatScanType,
  formatDate,
  exportScanJSON,
  exportScanPDF,
  exportScanMarkdown,
  exportScanHTML,
  rescanTarget,
  retriageScan,
  setFindingFalsePositive
} from '@/services/litesabre';
import EnhancedFindingDetail from '@/components/EnhancedFindingDetail';
import { FixPRDialog, SecurityOverview, DependenciesTab } from '@/components/EnhancedScanFeatures';
import { calculateConfidenceScores } from '@/services/litesabrePro';

const LiteSabreScanDetail = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [showFixPR, setShowFixPR] = useState(false);
  const [activeTab, setActiveTab] = useState('vulnerabilities');
  const [calculatingConfidence, setCalculatingConfidence] = useState(false);
  const [retriaging, setRetriaging] = useState(false);
  const [showFp, setShowFp] = useState(false);

  useEffect(() => {
    loadScan();
  }, [scanId]);

  // Auto-refresh when scan is in progress
  useEffect(() => {
    if (scan && (scan.status === 'pending' || scan.status === 'running' || scan.status === 'in_progress')) {
      const interval = setInterval(() => {
        loadScan();
      }, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [scan?.status]);

  const loadScan = async () => {
    try {
      setLoading(true);
      const data = await getScan(scanId);
      setScan(data);
    } catch (err) {
      console.error('Failed to load scan:', err);
      toast.error('Failed to load scan details');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleExportJSON = async () => {
    try {
      setExporting(true);
      await exportScanJSON(scanId);
      toast.success('JSON report downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export JSON report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      await exportScanPDF(scanId);
      toast.success('PDF report downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMarkdown = async () => {
    try {
      setExporting(true);
      await exportScanMarkdown(scanId);
      toast.success('Markdown report downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Markdown report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportHTML = async () => {
    try {
      setExporting(true);
      await exportScanHTML(scanId);
      toast.success('HTML report downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export HTML report');
    } finally {
      setExporting(false);
    }
  };

  const handleRescan = async () => {
    try {
      setRescanning(true);
      const result = await rescanTarget(scanId);
      toast.success('Rescan initiated');
      navigate(`${basePath}/scans/${result.new_scan_id}`);
    } catch (err) {
      console.error('Rescan failed:', err);
      toast.error('Failed to initiate rescan');
    } finally {
      setRescanning(false);
    }
  };

  const handleRetriage = async () => {
    try {
      setRetriaging(true);
      const res = await retriageScan(scanId);
      toast.success(`Re-triage done: ${res.retriage.false_positives} finding(s) marked as false positive`);
      await loadScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-triage failed');
    } finally {
      setRetriaging(false);
    }
  };

  const toggleFalsePositive = async (finding) => {
    const next = !finding.false_positive;
    try {
      await setFindingFalsePositive(scanId, finding.vulnerability_id, next);
      const update = (f) => (f.vulnerability_id === finding.vulnerability_id
        ? { ...f, false_positive: next, fp_reason: next ? 'Marked by reviewer' : null, fp_source: 'manual' } : f);
      const sevKey = `${String(finding.severity || 'unknown').toLowerCase()}_count`;
      const delta = next ? -1 : 1;
      setScan((s) => ({
        ...s,
        findings: s.findings.map(update),
        summary: s.summary ? {
          ...s.summary,
          [sevKey]: Math.max(0, (s.summary[sevKey] || 0) + delta),
          total_vulnerabilities: Math.max(0, (s.summary.total_vulnerabilities || 0) + delta),
        } : s.summary,
      }));
      setSelectedFinding((f) => (f ? update(f) : f));
      toast.success(next ? 'Marked as false positive' : 'Restored as a real finding');
    } catch (err) {
      toast.error('Could not update finding');
    }
  };

  const handleCalculateConfidence = async () => {
    try {
      setCalculatingConfidence(true);
      const result = await calculateConfidenceScores(scanId);
      toast.success(`Confidence scores calculated: avg ${result.average_confidence}%`);
      // Reload scan to get updated findings with scores
      await loadScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to calculate confidence scores');
    } finally {
      setCalculatingConfidence(false);
    }
  };

  const groupFindingsBySeverity = (findings) => {
    const groups = {
      critical: [],
      high: [],
      medium: [],
      low: [],
      unknown: []
    };
    
    findings?.forEach(f => {
      const sev = f.severity?.toLowerCase() || 'unknown';
      if (groups[sev]) {
        groups[sev].push(f);
      } else {
        groups.unknown.push(f);
      }
    });
    
    return groups;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Scan not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(`${basePath}/scans`)}>
          Back to Scans
        </Button>
      </div>
    );
  }

  const allFindings = scan.findings || [];
  const fpCount = allFindings.filter((f) => f.false_positive).length;
  const visibleFindings = showFp ? allFindings : allFindings.filter((f) => !f.false_positive);
  const groupedFindings = groupFindingsBySeverity(visibleFindings);
  const isScanning = scan.status === 'pending' || scan.status === 'running' || scan.status === 'in_progress';

  return (
    <div className="p-6 space-y-6">
      {/* Scan in Progress Banner */}
      {isScanning && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
          <div>
            <p className="font-medium text-blue-800">Scan in Progress</p>
            <p className="text-sm text-blue-600">Results will update automatically when the scan completes.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/scans`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{scan.name}</h1>
            <p className="text-gray-500 flex items-center gap-2">
              {formatScanType(scan.scan_type)} • {scan.target}
              <span className={`px-2 py-0.5 text-xs rounded-full ${scan.status === 'completed' ? 'bg-green-100 text-green-700' : scan.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {scan.status}
              </span>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRescan} 
            disabled={rescanning}
            data-testid="rescan-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${rescanning ? 'animate-spin' : ''}`} />
            {rescanning ? 'Rescanning...' : 'Rescan'}
          </Button>
          <Button
            variant="outline"
            onClick={handleRetriage}
            disabled={retriaging || isScanning}
            data-testid="retriage-btn"
            className="text-teal-700 border-teal-200 hover:bg-teal-50"
          >
            <Filter className={`w-4 h-4 mr-2 ${retriaging ? 'animate-pulse' : ''}`} />
            {retriaging ? 'Re-triaging...' : 'Re-triage'}
          </Button>
          <Button
            variant="outline"
            onClick={handleCalculateConfidence}
            disabled={calculatingConfidence}
            data-testid="calculate-confidence-btn"
            className="text-amber-600 border-amber-200 hover:bg-amber-50"
          >
            {calculatingConfidence ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Shield className="w-4 h-4 mr-2" />
            )}
            {calculatingConfidence ? 'Calculating...' : 'Confidence Scores'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowFixPR(true)}
            data-testid="fix-pr-btn"
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
          >
            <GitPullRequest className="w-4 h-4 mr-2" />
            Open Fix PR
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting}>
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Report'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportJSON}>
                <FileJson className="w-4 h-4 mr-2" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileText className="w-4 h-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportMarkdown}>
                <FileCode className="w-4 h-4 mr-2" />
                Export as Markdown
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportHTML} data-testid="export-html-btn">
                <Globe className="w-4 h-4 mr-2" />
                Export as HTML
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => navigate(`${basePath}/scan`)}>
            New Scan
          </Button>
          {scan?.target && (
            <Button
              variant="outline"
              onClick={() => navigate(`${basePath}/trends?target=${encodeURIComponent(scan.target)}`)}
              data-testid="view-trends-btn"
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Trends
            </Button>
          )}
        </div>
      </div>

      {/* Failure banner — shown when the scan finished with FAILED
          status. Without this the user sees five zero-count tiles and
          empty scan info (the Nexus-and-OSINT case from Feb 2026) and
          assumes it's a clean scan. */}
      {scan.status === 'failed' && (
        <Card className="border-red-300 bg-red-50" data-testid="scan-failed-banner">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-2 h-2 rounded-full bg-red-500 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-red-800">This scan did not complete successfully</p>
                <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">
                  {scan.error_message || 'The scan failed. Rerun with a valid target or check backend logs.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(scan.retriage || fpCount > 0) && (
        <Card className="border-teal-200 bg-teal-50/60" data-testid="retriage-banner">
          <CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-teal-900">
              <span className="font-semibold" data-testid="retriage-fp-count">{fpCount}</span> finding(s) marked as false positive and excluded from counts and reports.
              {scan.retriage?.at && <span className="text-teal-700"> Last re-triage: {formatDate(scan.retriage.at)}.</span>}
              {scan.retriage?.by_reason && (
                <ul className="mt-1 text-xs text-teal-800 list-disc ml-5">
                  {Object.entries(scan.retriage.by_reason).map(([r, n]) => <li key={r}>{r}: {n}</li>)}
                </ul>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowFp(!showFp)} data-testid="toggle-fp-btn">
              {showFp ? 'Hide false positives' : 'Show false positives'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-red-600">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-red-600">{scan.summary?.critical_count || 0}</p>
            <p className="text-sm text-gray-500">Critical</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-orange-600">{scan.summary?.high_count || 0}</p>
            <p className="text-sm text-gray-500">High</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-yellow-600">{scan.summary?.medium_count || 0}</p>
            <p className="text-sm text-gray-500">Medium</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-blue-600">{scan.summary?.low_count || 0}</p>
            <p className="text-sm text-gray-500">Low</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-green-600">{scan.summary?.fixable_count || 0}</p>
            <p className="text-sm text-gray-500">Fixable</p>
          </CardContent>
        </Card>
      </div>

      {/* Scan Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scan Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Target</p>
              <p className="font-medium truncate" title={scan.target}>{scan.target}</p>
            </div>
            <div>
              <p className="text-gray-500">Ecosystem</p>
              <p className="font-medium">{scan.ecosystem || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Packages Scanned</p>
              <p className="font-medium">{scan.summary?.total_packages || 0}</p>
            </div>
            <div>
              <p className="text-gray-500">Vulnerable Packages</p>
              <p className="font-medium">{scan.summary?.vulnerable_packages || 0}</p>
            </div>
            <div>
              <p className="text-gray-500">Started At</p>
              <p className="font-medium">{formatDate(scan.started_at)}</p>
            </div>
            <div>
              <p className="text-gray-500">Completed At</p>
              <p className="font-medium">{formatDate(scan.completed_at)}</p>
            </div>
            <div>
              <p className="text-gray-500">Source</p>
              <p className="font-medium capitalize">{scan.source?.replace('_', ' ') || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Linked Framework</p>
              <p className="font-medium">{scan.linked_framework_id ? 'Yes' : 'None'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Findings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Findings List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vulnerabilities ({visibleFindings.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="all">
                <TabsList className="w-full justify-start rounded-none border-b px-4">
                  <TabsTrigger value="all">All ({visibleFindings.length})</TabsTrigger>
                  <TabsTrigger value="critical" className="text-red-600">
                    Critical ({groupedFindings.critical.length})
                  </TabsTrigger>
                  <TabsTrigger value="high" className="text-orange-600">
                    High ({groupedFindings.high.length})
                  </TabsTrigger>
                  <TabsTrigger value="medium" className="text-yellow-600">
                    Medium ({groupedFindings.medium.length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="all" className="m-0">
                  <div className="divide-y max-h-[600px] overflow-auto">
                    {visibleFindings.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                        <p>No vulnerabilities found!</p>
                      </div>
                    ) : (
                      visibleFindings.map((finding, idx) => (
                        <div
                          key={finding.vulnerability_id || idx}
                          className={`p-4 hover:bg-gray-50 cursor-pointer transition ${finding.false_positive ? 'opacity-60' : ''} ${selectedFinding?.vulnerability_id === finding.vulnerability_id ? 'bg-indigo-50' : ''}`}
                          onClick={() => setSelectedFinding(finding)}
                          data-testid={`finding-row-${finding.vulnerability_id || idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-xs rounded border ${getSeverityBadgeColor(finding.severity)}`}>
                                  {finding.severity}
                                </span>
                                {finding.confidence_score && (
                                  <span className={`px-2 py-0.5 text-xs rounded ${finding.confidence_score >= 80 ? 'bg-emerald-100 text-emerald-700' : finding.confidence_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`} data-testid={`confidence-badge-${finding.vulnerability_id || idx}`}>
                                    {finding.confidence_score}% conf
                                  </span>
                                )}
                                {finding.cve_id && (
                                  <span className="text-xs text-gray-500 font-mono">{finding.cve_id}</span>
                                )}
                                {finding.false_positive && (
                                  <span className="px-2 py-0.5 text-xs rounded bg-slate-200 text-slate-700" title={finding.fp_reason} data-testid={`fp-badge-${finding.vulnerability_id || idx}`}>
                                    False positive
                                  </span>
                                )}
                              </div>
                              <p className="font-medium mt-1">{finding.title}</p>
                              {finding.package_name ? (
                                <p className="text-sm text-gray-500 mt-1">
                                  <span className="font-mono">{finding.package_name}</span>
                                  <span className="mx-1">@</span>
                                  <span className="font-mono">{finding.installed_version || 'unknown'}</span>
                                </p>
                              ) : finding.file_path ? (
                                <p className="text-sm text-gray-500 mt-1 font-mono truncate" title={finding.file_path}>
                                  {finding.file_path}
                                  {finding.line ? <span className="text-gray-400">:{finding.line}</span> : null}
                                </p>
                              ) : null}
                            </div>
                            {finding.fixed_version && (
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Fixed in</p>
                                <p className="text-sm font-mono text-green-600">{finding.fixed_version}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
                
                {['critical', 'high', 'medium'].map(severity => (
                  <TabsContent key={severity} value={severity} className="m-0">
                    <div className="divide-y max-h-[600px] overflow-auto">
                      {groupedFindings[severity].length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          No {severity} severity vulnerabilities
                        </div>
                      ) : (
                        groupedFindings[severity].map((finding, idx) => (
                          <div
                            key={finding.vulnerability_id || idx}
                            className={`p-4 hover:bg-gray-50 cursor-pointer transition ${selectedFinding?.vulnerability_id === finding.vulnerability_id ? 'bg-indigo-50' : ''}`}
                            onClick={() => setSelectedFinding(finding)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 text-xs rounded border ${getSeverityBadgeColor(finding.severity)}`}>
                                    {finding.severity}
                                  </span>
                                  {finding.cve_id && (
                                    <span className="text-xs text-gray-500 font-mono">{finding.cve_id}</span>
                                  )}
                                </div>
                                <p className="font-medium mt-1">{finding.title}</p>
                                {finding.package_name ? (
                                  <p className="text-sm text-gray-500 mt-1">
                                    <span className="font-mono">{finding.package_name}</span>
                                    <span className="mx-1">@</span>
                                    <span className="font-mono">{finding.installed_version || 'unknown'}</span>
                                  </p>
                                ) : finding.file_path ? (
                                  <p className="text-sm text-gray-500 mt-1 font-mono truncate" title={finding.file_path}>
                                    {finding.file_path}
                                    {finding.line ? <span className="text-gray-400">:{finding.line}</span> : null}
                                  </p>
                                ) : null}
                              </div>
                              {finding.fixed_version && (
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Fixed in</p>
                                  <p className="text-sm font-mono text-green-600">{finding.fixed_version}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Finding Detail Panel */}
        <div>
          <Card className="sticky top-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Vulnerability Details</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedFinding && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border bg-slate-50 p-2 text-xs text-slate-700" data-testid="fp-controls">
                  {selectedFinding.false_positive
                    ? <span>False positive: {selectedFinding.fp_reason}</span>
                    : <span>Reviewed and not a real issue?</span>}
                  <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => toggleFalsePositive(selectedFinding)} data-testid="toggle-finding-fp-btn">
                    {selectedFinding.false_positive ? 'Restore finding' : 'Mark false positive'}
                  </Button>
                </div>
              )}
              <EnhancedFindingDetail finding={selectedFinding} scanId={scanId} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Security Overview */}
      <SecurityOverview scanId={scanId} />

      {/* Dependencies Tab with License Info */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" />
            Dependencies & Licenses
          </CardTitle>
          <CardDescription>
            Detailed dependency information with license analysis and health metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DependenciesTab scanId={scanId} />
        </CardContent>
      </Card>

      {/* Fix PR Dialog */}
      <FixPRDialog 
        open={showFixPR} 
        onClose={() => setShowFixPR(false)} 
        scanId={scanId} 
      />
    </div>
  );
};

export default LiteSabreScanDetail;
